import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PageSection, ReportTable, StatusBadge } from "../components/reporting";
import type { Column } from "../components/reporting";
import { roles, roleMeta, userStateMeta } from "../data/users";
import type { BrandUser, RoleId } from "../data/users";
import { useSession } from "../session";
import { useScreenInit } from "../useScreenInit";
import { api } from "../api";

const columns: Column[] = [
  { label: "User", align: "left" },
  { label: "Access level", align: "left", width: "190px" },
  { label: "Last active", align: "left", width: "170px" },
  { label: "Status", align: "left", width: "130px" },
  { label: "Actions", align: "right", width: "250px" },
];

const textCls =
  "w-full h-9 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors";

function AccessLevelMenu({
  userName,
  value,
  disabled,
  onChange,
}: {
  userName: string;
  value: Exclude<RoleId, "owner">;
  disabled: boolean;
  onChange: (role: Exclude<RoleId, "owner">) => Promise<void>;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, width: 0 });
  const options = roles.filter(
    (role): role is (typeof roles)[number] & { id: Exclude<RoleId, "owner"> } =>
      role.id !== "owner",
  );

  useEffect(() => {
    if (!open || !buttonRef.current) return;

    const positionMenu = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuHeight = options.length * 45;
      const opensUp = rect.bottom + menuHeight + 8 > window.innerHeight;
      setMenuPosition({
        left: rect.left,
        top: opensUp ? rect.top - menuHeight - 6 : rect.bottom + 6,
        width: Math.max(rect.width, 150),
      });
    };

    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!buttonRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };

    positionMenu();
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, options.length]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Change access level for ${userName}`}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className="h-9 min-w-[130px] px-3 rounded-xs border border-default bg-surface-white text-body text-primary text-left focus:outline-none focus:border-brand-red/50 transition-colors inline-flex items-center justify-between gap-3 hover:bg-surface-light disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span>{roleMeta[value].label}</span>
        <i
          className={`fa-solid fa-chevron-down inline-block text-[10px] text-tertiary transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={`Access level for ${userName}`}
            className="fixed z-[100] overflow-hidden rounded-md border border-default bg-surface-white shadow-[var(--shadow-md)]"
            style={menuPosition}
          >
            {options.map((option) => {
              const selected = option.id === value;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    setOpen(false);
                    if (!selected) void onChange(option.id);
                  }}
                  className={`w-full px-3 py-2.5 text-left border-b border-default last:border-b-0 transition-colors flex items-center gap-2.5 ${
                    selected ? "bg-brand-red-tint" : "hover:bg-surface-light"
                  }`}
                >
                  <span
                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                      selected ? "border-brand-red bg-brand-red" : "border-default"
                    }`}
                  >
                    {selected && <i className="fa-solid fa-check text-[8px] text-white" />}
                  </span>
                  <span className="text-body font-medium text-primary">{option.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

function InviteForm({
  onCancel,
  onInvited,
  canAssignOwner,
  changingOwner = false,
}: {
  onCancel: () => void;
  onInvited: () => Promise<void>;
  canAssignOwner: boolean;
  changingOwner?: boolean;
}) {
  const [role, setRole] = useState<RoleId>(changingOwner ? "owner" : "analyst");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const availableRoles = changingOwner
    ? roles.filter((candidate) => candidate.id === "owner")
    : roles.filter((candidate) => candidate.id !== "owner" || canAssignOwner);

  const invite = async () => {
    setSaving(true);
    setError("");
    try {
      await api("/api/team/invitations", {
        method: "POST",
        body: JSON.stringify({ name, email, role }),
      });
      await onInvited();
      onCancel();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Invite failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageSection title={changingOwner ? "Change Brand owner" : "Invite a user"}>
      <div className="flex flex-col gap-4 max-w-[760px]">
        {changingOwner && (
          <p className="text-caption text-secondary">
            The current Brand owner will become a Manager. The new owner will receive the standard
            24-hour account setup email if they have not used Leads Manager before.
          </p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-caption font-medium text-primary">Full name</label>
            <input
              className={textCls}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Hannah Cole"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-caption font-medium text-primary">Work email</label>
            <input
              className={textCls}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.test"
            />
          </div>
        </div>

        {error && <p className="text-caption text-status-red">{error}</p>}

        <div>
          <span className="text-caption font-medium text-primary">Access level</span>
          <div className="grid grid-cols-2 gap-3 mt-2">
            {availableRoles.map((r) => {
              const on = role === r.id;
              return (
                <button
                  key={r.id}
                  onClick={() => setRole(r.id)}
                  className={`flex flex-col gap-2 rounded-md border px-4 py-3.5 text-left transition-colors ${
                    on
                      ? "border-brand-red bg-brand-red-tint"
                      : "border-default hover:bg-surface-light"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <span
                      className={`w-[15px] h-[15px] rounded-full border flex items-center justify-center shrink-0 ${
                        on ? "border-brand-red" : "border-default"
                      }`}
                    >
                      {on && <span className="w-[7px] h-[7px] rounded-full bg-brand-red" />}
                    </span>
                    <span className="text-body font-semibold text-primary">{r.label}</span>
                  </span>
                  <span className="text-caption text-tertiary">{r.summary}</span>
                  <span className="flex flex-wrap gap-1.5 pt-0.5">
                    {r.permissions.map((p) => (
                      <span
                        key={p}
                        className="text-micro text-secondary bg-surface-light border border-default rounded-xs px-1.5 py-0.5"
                      >
                        {p}
                      </span>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
          {role === "owner" && !changingOwner && (
            <p className="mt-2 text-caption text-secondary">
              Assigning a new Brand owner will move the current owner to Manager access.
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={invite}
            disabled={saving || !name || !email}
            className="h-10 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2 whitespace-nowrap shrink-0"
          >
            <i className="fa-solid fa-paper-plane text-[12px]" />
            {saving ? "Sending…" : changingOwner ? "Change owner and send invite" : "Send invite"}
          </button>
          <button
            onClick={onCancel}
            className="h-10 px-5 rounded-xs border border-default text-secondary text-body font-medium hover:bg-surface-white transition-colors"
          >
            Cancel
          </button>
          <span className="text-caption text-tertiary ml-2">
            They'll receive one account setup link, valid for 24 hours. Opening an expired link
            automatically sends a replacement to the invited address.
          </span>
        </div>
      </div>
    </PageSection>
  );
}

export function Team() {
  const seed = useScreenInit();
  const session = useSession();
  const [inviting, setInviting] = useState((seed.inviting as boolean) ?? false);
  const [changingOwner, setChangingOwner] = useState(false);
  const [users, setUsers] = useState<BrandUser[]>([]);
  const [loadError, setLoadError] = useState("");
  const [removingUser, setRemovingUser] = useState<BrandUser | null>(null);
  const [removalError, setRemovalError] = useState("");
  const [removalSaving, setRemovalSaving] = useState(false);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [inviteNotice, setInviteNotice] = useState("");
  const canManage = session.role === "owner";

  const refresh = async () => {
    try {
      const response = await api<{
        items: Array<{
          id: string;
          name: string;
          email: string;
          role: RoleId;
          emailVerified: boolean;
          lastActive: string | null;
        }>;
      }>("/api/team");
      setUsers(
        response.items.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          state: user.emailVerified ? "active" : "invited",
          lastActive: user.lastActive ? new Date(user.lastActive).toLocaleString("en-GB") : "Never",
        })),
      );
    } catch (problem) {
      setLoadError(problem instanceof Error ? problem.message : "Team could not be loaded");
    }
  };
  useEffect(() => {
    void refresh();
  }, []);

  const changeRole = async (id: string, role: RoleId) => {
    const password = window.prompt("Confirm your password to change this role");
    if (!password) return;
    const reauth = await api<{ token: string }>("/api/reauthenticate", {
      method: "POST",
      body: JSON.stringify({ password, action: "member.role" }),
    });
    await api(`/api/team/${encodeURIComponent(id)}/role`, {
      method: "PUT",
      body: JSON.stringify({ role, reauthenticationToken: reauth.token }),
    });
    await refresh();
  };

  const removeUser = async () => {
    if (!removingUser) return;
    setRemovalSaving(true);
    setRemovalError("");
    try {
      await api(`/api/team/${encodeURIComponent(removingUser.id)}`, { method: "DELETE" });
      await refresh();
      setRemovingUser(null);
    } catch (problem) {
      setRemovalError(problem instanceof Error ? problem.message : "User could not be removed");
    } finally {
      setRemovalSaving(false);
    }
  };

  const resendInvite = async (user: BrandUser) => {
    setResendingUserId(user.id);
    setInviteNotice("");
    setLoadError("");
    try {
      await api(`/api/team/${encodeURIComponent(user.id)}/resend-invite`, { method: "POST" });
      setInviteNotice(`A fresh 24-hour setup link has been sent to ${user.name}.`);
    } catch (problem) {
      setLoadError(problem instanceof Error ? problem.message : "Invite could not be resent");
    } finally {
      setResendingUserId(null);
    }
  };

  const rows = users.map((u) => {
    const st = userStateMeta[u.state];
    return [
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 shrink-0 rounded-full bg-surface-info text-brand-dark-teal flex items-center justify-center text-micro font-semibold">
          {u.name
            .split(" ")
            .map((n) => n[0])
            .join("")}
        </span>
        <div className="min-w-0">
          <div className="text-body font-medium text-primary truncate">{u.name}</div>
          <div className="text-micro text-tertiary truncate">{u.email}</div>
        </div>
      </div>,
      u.role === "owner" ? (
        <span className="text-body text-secondary">{roleMeta.owner.label}</span>
      ) : (
        <AccessLevelMenu
          userName={u.name}
          value={u.role}
          disabled={!canManage}
          onChange={(role) => changeRole(u.id, role)}
        />
      ),
      <span className="text-body text-secondary whitespace-nowrap">{u.lastActive}</span>,
      <StatusBadge label={st.label} tone={st.tone} />,
      canManage ? (
        <div className="flex items-center justify-end gap-2">
          {u.state === "invited" && (
            <button
              type="button"
              disabled={resendingUserId === u.id}
              onClick={() => void resendInvite(u)}
              className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption font-medium text-primary hover:bg-surface-light transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-50"
              aria-label={`Resend invite to ${u.name}`}
            >
              <i className="fa-solid fa-paper-plane text-[11px]" aria-hidden="true" />
              {resendingUserId === u.id ? "Sending…" : "Resend invite"}
            </button>
          )}
          {session.adminTenantAccess && u.role === "owner" && (
            <button
              type="button"
              onClick={() => {
                setChangingOwner(true);
                setInviting(true);
              }}
              className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption font-medium text-primary hover:bg-surface-light transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
              aria-label={`Change Brand owner from ${u.name}`}
            >
              <i className="fa-solid fa-user-pen text-[11px]" aria-hidden="true" />
              Change owner
            </button>
          )}
          {u.role !== "owner" && (
            <button
              type="button"
              onClick={() => {
                setRemovalError("");
                setRemovingUser(u);
              }}
              className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption font-medium text-status-red hover:bg-[rgba(255,101,80,0.08)] hover:border-status-red/30 transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
              aria-label={`Remove ${u.name}`}
            >
              <i className="fa-solid fa-trash-can text-[11px]" aria-hidden="true" />
              Remove
            </button>
          )}
        </div>
      ) : (
        <span />
      ),
    ];
  });

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-6">
          {[
            { v: users.filter((u) => u.state === "active").length, l: "Active users" },
            { v: users.filter((u) => u.state === "invited").length, l: "Invites pending" },
          ].map((m) => (
            <div key={m.l} className="flex items-center gap-2.5">
              <span className="text-title-md font-bold text-primary leading-none">{m.v}</span>
              <span className="text-caption text-tertiary">{m.l}</span>
            </div>
          ))}
        </div>
        {!inviting && (
          <button
            onClick={() => {
              setChangingOwner(false);
              setInviting(true);
            }}
            disabled={!canManage}
            className="h-9 px-3.5 rounded-xs bg-brand-red text-on-brand text-body font-medium flex items-center gap-2 hover:bg-brand-red-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="fa-solid fa-user-plus text-[12px]" />
            Invite user
          </button>
        )}
      </div>

      {inviting && (
        <InviteForm
          key={changingOwner ? "change-owner" : "invite-user"}
          canAssignOwner={session.adminTenantAccess}
          changingOwner={changingOwner}
          onCancel={() => {
            setInviting(false);
            setChangingOwner(false);
          }}
          onInvited={async () => {
            await refresh();
            if (changingOwner) {
              setInviteNotice(
                "The Brand owner has been changed. Account setup has been sent where required.",
              );
            }
          }}
        />
      )}
      {inviteNotice && (
        <p
          role="status"
          className="rounded-md border border-[rgba(86,205,174,0.35)] bg-[rgba(86,205,174,0.1)] px-4 py-3 text-caption text-primary"
        >
          <i className="fa-solid fa-circle-check mr-2 text-status-green" aria-hidden="true" />
          {inviteNotice}
        </p>
      )}
      {loadError && <p className="text-caption text-status-red">{loadError}</p>}

      <PageSection title={`${users.length} users`} bodyClassName="px-[20px] pt-[16px] pb-[20px]">
        <ReportTable columns={columns} rows={rows} />
      </PageSection>

      <PageSection title="What each level can do">
        <div className="flex flex-col">
          {roles.map((r, i) => (
            <div
              key={r.id}
              className={`flex items-start gap-5 py-3.5 ${i > 0 ? "border-t border-default" : ""}`}
            >
              <div className="w-[150px] shrink-0">
                <div className="text-body font-semibold text-primary">{r.label}</div>
                <div className="text-micro text-tertiary mt-0.5">{r.summary}</div>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {r.permissions.map((p) => (
                  <span
                    key={p}
                    className="text-caption text-secondary bg-surface-light border border-default rounded-xs px-2 py-1"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PageSection>

      {removingUser &&
        createPortal(
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-5">
            <button
              type="button"
              aria-label="Cancel user removal"
              className="absolute inset-0 bg-black/35"
              onClick={() => {
                if (!removalSaving) setRemovingUser(null);
              }}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="remove-user-title"
              className="relative w-full max-w-[440px] rounded-xl border border-default bg-surface-white p-6 shadow-[var(--shadow-md)]"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgba(255,101,80,0.12)] text-status-red">
                <i className="fa-solid fa-user-minus text-[15px]" aria-hidden="true" />
              </div>
              <h2 id="remove-user-title" className="mt-4 text-title-md font-bold text-primary">
                Remove {removingUser.name}?
              </h2>
              <p className="mt-2 text-body text-secondary">
                They will immediately lose access to {session.brand}. Any pending account setup link
                will no longer grant access to this brand.
              </p>
              {removalError && (
                <p role="alert" className="mt-3 text-caption text-status-red">
                  {removalError}
                </p>
              )}
              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  disabled={removalSaving}
                  onClick={() => setRemovingUser(null)}
                  className="h-10 px-4 rounded-xs border border-default bg-surface-white text-body font-medium text-secondary hover:bg-surface-light transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={removalSaving}
                  onClick={() => void removeUser()}
                  className="h-10 px-4 rounded-xs bg-status-red text-white text-body font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center gap-2"
                >
                  <i className="fa-solid fa-trash-can text-[11px]" aria-hidden="true" />
                  {removalSaving ? "Removing…" : "Remove user"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
